import { noOp } from 'common/Constants';
import { formatGqlErrors } from 'common/GqlUtilities';
import { useCallback, useEffect, useState } from 'react';
import { processImage } from 'src/shared/design-system/FileUpload/filePreprocessor';
import { FileEntry } from 'src/shared/design-system/FileUpload/FileUpload';
import { useFileSubmitHook } from 'src/shared/design-system/FileUpload/useFileUploadSubmitHook';
import Notifications from 'src/shared/Notifications';
import { match } from 'ts-pattern';
import { getPhotoPublicUrl } from './utils';

interface IUploadProps {
  autoUpload?: boolean;
  /** If using multiple uploaders, you can pass in an id to identify the uploader callback. */
  id?: string;
  /** Message to show when trying to upload an invalid file. */
  invalidFileMessage?: string;
  /**
   * Callback when value of uploader changes
   * @param { String } photoUrl: The url of the photo.
   * @param { String } id: The id of the uploader.
   * @param { File } file: The file of the image.
   * @returns void
   */
  onChange: (photoUrl: string, id: string, file: File) => void;
  /** Similar to `disabled` but does not change appearance to disabled. Prevents changing the current image selection. */
  readOnly?: boolean;
  /** URL value of image to show. */
  value: string | null | undefined;
}

/**
 * Processes image for uploading.
 */
export const usePreprocessImage = (invalidFileMessage?: string) => {
  const preprocessImage = useCallback(
    async (blob: File): Promise<File> => {
      try {
        const convertedBlob = await processImage(blob);
        return convertedBlob;
      } catch (e) {
        console.error(e);
        Notifications.error(invalidFileMessage || 'Invalid file type.');
      }
      return blob;
    },
    [invalidFileMessage]
  );
  return { preprocessImage };
};

/**
 * Processes and submits photos to shef-general.
 */
export const useSubmitPhotos = (invalidFileMessage?: string) => {
  const { preprocessImage } = usePreprocessImage(invalidFileMessage);
  const { submitFiles } = useFileSubmitHook({
    preprocess: preprocessImage,
    bucket: 'shef-general',
    path: 'uploads',
  });
  return { submitPhotos: submitFiles };
};

export const useSumbitPreprocessedPhotos = (invalidFileMessage?: string) => {
  const { submitPhotos } = useSubmitPhotos(invalidFileMessage);
  return {
    submitPhotos: (processedFile) =>
      submitPhotos(processedFile)
        .then(([file]) => getPhotoPublicUrl(file))
        .catch((error) => {
          Notifications.error(formatGqlErrors(error));
          return null;
        }),
  };
};

/**
 * Hook for photo upload component.
 */
export const usePhotoUpload = ({ autoUpload, id, invalidFileMessage, onChange, value }: IUploadProps) => {
  const [imageSrc, setImageSrc] = useState(value);
  const [isUploading, setIsUploading] = useState(false);
  const { preprocessImage } = usePreprocessImage(invalidFileMessage);
  const { submitPhotos } = useSubmitPhotos(invalidFileMessage);

  useEffect(() => {
    if (!imageSrc && imageSrc !== value) {
      setImageSrc(value);
    }
  }, [imageSrc, setImageSrc, value]);

  /**
   * Fires callback function after upload is complete
   */
  const onUploadComplete = useCallback(
    async (file: FileEntry, originalFile: File) => {
      const publicUrl = getPhotoPublicUrl(file);
      if (publicUrl) {
        onChange(publicUrl, id || '', originalFile);
        setImageSrc(publicUrl);
      }
      setIsUploading(false);
    },
    [id, onChange]
  );

  const handleChange = useCallback(
    async (fileInput) => {
      const originalFile = fileInput[0];
      await match({ hasFile: !!originalFile, autoUpload, originalFile })
        .with({ hasFile: true, autoUpload: true }, async () => {
          setIsUploading(true);
          submitPhotos(fileInput)
            .then(([file]) => onUploadComplete(file, originalFile))
            .catch((error) => {
              setIsUploading(false);
              Notifications.error(formatGqlErrors(error));
            });
        })
        .with({ hasFile: true }, async () => {
          const processedFile = await preprocessImage(originalFile);
          const preview = URL.createObjectURL(processedFile);
          Object.assign(processedFile, { preview });
          onChange(preview, id || '', processedFile);
          setImageSrc(preview);
        })
        .otherwise(noOp);
    },
    [autoUpload, id, onChange, onUploadComplete, preprocessImage, setImageSrc, submitPhotos]
  );

  return {
    handleChange,
    isUploading,
    imageSrc,
    maxSize: 30 * 1024 * 1024,
  };
};
